import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertTriangle, Mail } from 'lucide-react';
import icon from '../../img/icon.png';
import { useLanguage } from '../../hooks/useLanguage';

const EmailVerificationSuccess: React.FC = () => {
    const { t } = useTranslation();
    const { isRTL } = useLanguage();
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // Récupérer les paramètres d'URL
    const status = searchParams.get('status') || 'loading';
    const message = searchParams.get('message') || '';

    const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>(
        status === 'success' ? 'success' : status === 'error' ? 'error' : 'loading'
    );
    const [errorMessage, setErrorMessage] = useState<string>(
        message ? decodeURIComponent(message).replace(/_/g, ' ') : t('emailVerification.error.defaultMessage')
    );

    // Si le status n'est pas présent dans l'URL, on effectue la vérification côté client
    useEffect(() => {
        if (status !== 'success' && status !== 'error') {
            // Le backend a été mis à jour pour ne pas avoir à faire cette vérification
            // côté client, mais nous laissons ce code en commentaire au cas où
            /*
            const verifyEmail = async () => {
              try {
                // Vérification via API
                // ...
                setVerificationStatus('success');
              } catch (error: any) {
                setVerificationStatus('error');
                setErrorMessage(error.response?.data?.message || t('emailVerification.error.invalidToken'));
              }
            };
            verifyEmail();
            */
        }
    }, [status, token, t]);

    const handleLogin = () => {
        navigate('/login');
    };

    const handleResendVerification = () => {
        navigate('/login', { state: { showResendOption: true } });
    };

    return (
        <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8 ${isRTL ? 'rtl' : ''}`}>
            <div className="max-w-lg mx-auto">
                <div className="bg-white rounded-xl shadow-xl px-8 py-12 text-center">
                    <img src={icon} alt="Inspecly Logo" className="h-20 w-20 mx-auto" />

                    {verificationStatus === 'loading' && (
                        <div className="mt-6">
                            <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                            <h2 className="mt-6 text-2xl font-bold text-gray-900">
                                {t('emailVerification.loading.title')}
                            </h2>
                            <p className="mt-2 text-gray-600">
                                {t('emailVerification.loading.description')}
                            </p>
                        </div>
                    )}

                    {verificationStatus === 'success' && (
                        <>
                            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mt-6" />
                            <h1 className="mt-6 text-3xl font-bold text-gray-900">
                                {t('emailVerification.success.title')}
                            </h1>
                            <p className="mt-4 text-lg text-gray-600">
                                {t('emailVerification.success.description')}
                            </p>
                            <div className="mt-8">
                                <button
                                    onClick={handleLogin}
                                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-[#115DF2] hover:bg-[#0E4BC2] transition-colors"
                                >
                                    {t('emailVerification.actions.login')}
                                </button>
                            </div>
                        </>
                    )}

                    {verificationStatus === 'error' && (
                        <>
                            <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mt-6" />
                            <h1 className="mt-6 text-3xl font-bold text-gray-900">
                                {t('emailVerification.error.title')}
                            </h1>
                            <p className="mt-4 text-lg text-red-600">
                                {errorMessage}
                            </p>
                            <div className="mt-6 p-4 bg-amber-50 rounded-lg text-left">
                                <h3 className="font-medium text-amber-800">
                                    {t('emailVerification.error.possibleReasons.title')}
                                </h3>
                                <ul className="mt-2 space-y-2 text-gray-600">
                                    <li className="flex items-start">
                                        <span className={`text-amber-500 ${isRTL ? 'ml-2' : 'mr-2'}`}>•</span>
                                        {t('emailVerification.error.possibleReasons.expired')}
                                    </li>
                                    <li className="flex items-start">
                                        <span className={`text-amber-500 ${isRTL ? 'ml-2' : 'mr-2'}`}>•</span>
                                        {t('emailVerification.error.possibleReasons.alreadyUsed')}
                                    </li>
                                    <li className="flex items-start">
                                        <span className={`text-amber-500 ${isRTL ? 'ml-2' : 'mr-2'}`}>•</span>
                                        {t('emailVerification.error.possibleReasons.invalidToken')}
                                    </li>
                                </ul>
                            </div>
                            <div className="mt-8 space-y-3">
                                <button
                                    onClick={handleResendVerification}
                                    className="inline-flex items-center justify-center w-full px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-[#115DF2] hover:bg-[#0E4BC2] transition-colors"
                                >
                                    <Mail className={`${isRTL ? 'ml-2' : 'mr-2'} h-5 w-5`} />
                                    {t('emailVerification.actions.resendVerification')}
                                </button>
                                <button
                                    onClick={handleLogin}
                                    className="inline-flex items-center justify-center w-full px-6 py-3 border border-gray-300 text-base font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                >
                                    {t('emailVerification.actions.backToLogin')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmailVerificationSuccess;